import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation } from "react-router";
import Loader from "react-loader-spinner";
import pageLeft from "../../../assets/images/CounselorCurrent/Page/left.png";
import pageLeftHide from "../../../assets/images/CounselorCurrent/Page/left-hide.png";
import pageRight from "../../../assets/images/CounselorCurrent/Page/right.png";
import pageCenter from "../../../assets/images/CounselorCurrent/Page/center.png";
import QueryService from "../../../services/QueryService";
import CustomLoader from "../../customLoader/CustomLoader";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import swal from "sweetalert";
import MyStudentTable from "./MyStudentTable";
import {
  applySelectionChange,
  getSelectionModelForPage,
} from "../../../utils/queryResultSelection";
import "../../queryresult/QueryResult.css";

const MyStudentList = () => {
  const history = useHistory();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [paginationCount, setPaginationCount] = useState();
  const [loading, setLoading] = useState(false);
  const [loaderForExport, setLoaderForExport] = useState(false);
  const [saveSelection, setSaveSelection] = useState([]);
  const [newSelectedIds, setNewSelectedIds] = useState([]);
  const [queryStudentData, setQueryStudentData] = useState();
  const [selectAll, setSelectAll] = useState(false);
  const from = location?.state?.fromSavedQueriedResult;
  const [sortModel, setSortModel] = useState([]);
  const savedSelectionRef = useRef([]);
  const shouldRestoreSelectionRef = useRef(false);

  const fieldMap = {
    "0": "email",
    "1": "first_name",
    "2": "last_name",
    "3": "ethnicity",
    "4": "gender",
    "5": "gpa",
    "6": "grade",
    "7": "school_name",
  };

  const normalizeStudentRows = (list) => {
    const seen = new Set();
    return (Array.isArray(list) ? list : []).reduce((acc, row, idx) => {
      const schoolId = row?.[9] ?? row?.["9"];
      const studentId = row?.[10] ?? row?.["10"];
      const legacyId = row?.id ?? row?.[8] ?? row?.["8"];
      const dedupeKey =
        schoolId != null && studentId != null
          ? `${schoolId}-${studentId}`
          : String(legacyId ?? idx);
      if (seen.has(dedupeKey)) return acc;
      seen.add(dedupeKey);
      const derivedId =
        schoolId != null && studentId != null
          ? dedupeKey
          : legacyId ?? idx;
      const { id: _id, 8: _legacyId, ...rest } = row || {};
      acc.push({
        id: derivedId,
        ...rest,
      });
      return acc;
    }, []);
  };

  const getFavStudentData = () => {
    setLoading(true);
    const firstSort =
      Array.isArray(sortModel) && sortModel.length > 0 ? sortModel[0] : null;
    const params = {
      page,
      ...(firstSort
        ? {
            field: fieldMap[firstSort.field] || firstSort.field,
            direction: firstSort.sort === "asc" ? "asc" : "desc",
          }
        : {}),
    };

    QueryService.getFavoritePage(params)
      .then((data) => {
        const payload = data?.data || {};
        setQueryStudentData(payload);
        const list = Array.isArray(payload.student_list)
          ? payload.student_list
          : [];
        setTableData(normalizeStudentRows(list));
        setPaginationCount(payload.total_pages);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log("error from the service", error);
        setTableData([]);
      });
  };

  useEffect(() => {
    getFavStudentData();
  }, [sortModel, page]);

  const downloadFile = (downloadURL, fileName) => {
    const downloadLink = document.createElement("a");
    document.body.appendChild(downloadLink);
    downloadLink.download = fileName;
    downloadLink.href = downloadURL;
    downloadLink.click();
  };

  const deleteTranscript = () => {
    let payload;
    const n2 = {};
    newSelectedIds.forEach((i, index) => {
      n2[index] = { school_id: i["9"], student_id: i["10"] };
    });
    if (saveSelection === "selected All") {
      payload = { is_all: true };
    } else {
      payload = { is_all: false, details: n2 };
    }

    QueryService.deleteTrans(payload)
      .then(() => {
        swal({
          text: `Students Deleted Successfully from My Student List.`,
          icon: "success",
          buttons: { OK: true },
          closeOnClickOutside: false,
        }).then((value) => {
          if (value === "OK") {
            window.location.reload();
          }
        });
      })
      .catch((error) => {
        console.log("error from save student service", error);
      });
  };

  const downloadTranscript = () => {
    setLoaderForExport(true);
    let payload;
    const n2 = {};
    newSelectedIds.forEach((i, index) => {
      n2[index] = { school_id: i["9"], student_id: i["10"] };
    });
    if (saveSelection === "selected All") {
      payload = { is_all: true };
    } else {
      payload = { is_all: false, details: n2 };
    }
    QueryService.downloadTrans(payload)
      .then((data) => {
        downloadFile(data.data.download, "My File");
        setLoaderForExport(false);
      })
      .catch((error) => {
        console.log("error from save student service", error);
        setLoaderForExport(false);
      });
  };

  const onExportClick = () => {
    const hasSelection =
      saveSelection === "selected All" ||
      (Array.isArray(saveSelection) && saveSelection.length > 0);
    if (!hasSelection) {
      swal({
        text: "Please select at least one record to export.",
        icon: "warning",
        dangerMode: true,
        buttons: { OK: true },
        closeOnClickOutside: false,
      });
      return;
    }
    downloadTranscript();
  };

  useEffect(() => {
    if (!shouldRestoreSelectionRef.current || loading) return;
    shouldRestoreSelectionRef.current = false;
    setSaveSelection(savedSelectionRef.current);
  }, [tableData, loading]);

  const pageSelectionModel = useMemo(
    () => getSelectionModelForPage(saveSelection, tableData),
    [saveSelection, tableData]
  );

  const pageSelectionSet = useMemo(
    () => new Set(pageSelectionModel),
    [pageSelectionModel]
  );

  const totalStudentCount = useMemo(
    () => queryStudentData?.num_students ?? tableData.length,
    [queryStudentData, tableData]
  );

  const selectedCount = useMemo(() => {
    if (saveSelection === "selected All") {
      return totalStudentCount;
    }
    return Array.isArray(saveSelection) ? saveSelection.length : 0;
  }, [saveSelection, totalStudentCount]);

  const sortField = sortModel[0]?.field;
  const sortDirection = sortModel[0]?.sort || "asc";

  const onSelection = useCallback(
    (ids) => {
      const { nextSelection, selectAll: nextSelectAll } = applySelectionChange({
        ids,
        tableData,
        saveSelection,
        page,
        from,
      });

      setSaveSelection(nextSelection);
      savedSelectionRef.current = nextSelection;
      if (nextSelection === "selected All") {
        setNewSelectedIds(tableData);
      } else if (Array.isArray(nextSelection)) {
        setNewSelectedIds(nextSelection);
      } else {
        setNewSelectedIds([]);
      }
      setSelectAll(nextSelectAll);
    },
    [tableData, saveSelection, page, from]
  );

  const handleRowToggle = useCallback(
    (row, selecting) => {
      const rowId = String(row.id);
      const currentPageIds = [...pageSelectionModel];
      const newPageIds = selecting
        ? currentPageIds.includes(rowId)
          ? currentPageIds
          : [...currentPageIds, rowId]
        : currentPageIds.filter((id) => id !== rowId);
      onSelection(newPageIds);
    },
    [pageSelectionModel, onSelection]
  );

  const handleSelectAllToggle = useCallback(() => {
    const isGlobalAll = saveSelection === "selected All";
    const allOnPageSelected =
      tableData.length > 0 &&
      tableData.every((row) => pageSelectionSet.has(String(row.id)));

    if (isGlobalAll || allOnPageSelected) {
      onSelection([]);
      return;
    }

    setSaveSelection("selected All");
    savedSelectionRef.current = "selected All";
    setSelectAll(true);
    setNewSelectedIds(tableData);
  }, [saveSelection, tableData, pageSelectionSet, onSelection]);

  const handleSort = useCallback(
    (column, direction) => {
      const rule = { field: column.sortField, sort: direction };
      setSortModel([rule]);
      if (page !== 1) {
        setPage(1);
      }
    },
    [page]
  );

  const hasSelection =
    saveSelection === "selected All" ||
    (Array.isArray(saveSelection) && saveSelection.length > 0);

  return (
    <Fragment>
      <div className="my-student-panel">
        <p className="my-student-panel__title bold">Student Records</p>
        {loading ? (
          <CustomLoader />
        ) : (
          <div
            className={`qr-table-shell my-student-panel__table ${
              paginationCount && paginationCount > 1
                ? "table-container"
                : "table-container-less"
            }`}
          >
            <MyStudentTable
              tableData={tableData}
              saveSelection={saveSelection}
              pageSelectionSet={pageSelectionSet}
              onRowToggle={handleRowToggle}
              onSelectAllToggle={handleSelectAllToggle}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              selectedCount={selectedCount}
            />
          </div>
        )}

        <div className="qr-footer-topline" />
        <div className="result-button-container my-student-panel__actions">
          <button
            onClick={onExportClick}
            style={{
              justifyContent: "center",
              background: "#1395F2",
              borderColor: "#1395F2",
              color: "#fff",
            }}
            className="qr-footer-btn flex"
          >
            <img
              src={require("../../../assets/images/premiumService/export.png")}
              alt=""
              className="btn-icon"
            />
            Export Transcript
            {loaderForExport && (
              <span>
                <Loader type="TailSpin" color="#E36221" height={20} width={20} />
              </span>
            )}
          </button>
          <button
            onClick={() => deleteTranscript()}
            disabled={!hasSelection}
            className="btn-advance-single"
            style={{
              position: "relative",
              padding: "14px 26px",
              borderRadius: "20px",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "20px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              width: "220px",
              flex: "0 0 220px",
              justifyContent: "center",
              cursor: hasSelection ? "pointer" : "not-allowed",
              opacity: hasSelection ? 1 : 0.5,
            }}
          >
            <img
              src={require("../../../assets/images/premiumService/delete.png")}
              alt=""
              className="btn-icon"
            />
            Delete
          </button>
          <button
            className="qr-footer-btn qr-footer-btn-secondary"
            onClick={() => history.push("/recruiter/search-query")}
          >
            <img
              src={require("../../../assets/images/queryResult/close.png")}
              alt=""
              className="btn-icon"
            />
            Cancel
          </button>
        </div>
        <br />
        {(() => {
          const totalPages = paginationCount || 0;
          return totalPages >= 1 ? (
            <div className="template-pagination-container">
              <div className="custom-pagination" style={{ marginLeft: "auto" }}>
                <button
                  type="button"
                  aria-label="Previous"
                  className={`pagination-btn ${page === 1 ? "disabled" : ""}`}
                  onClick={() => {
                    if (page > 1) {
                      savedSelectionRef.current = saveSelection;
                      shouldRestoreSelectionRef.current = true;
                      setPage(page - 1);
                    }
                  }}
                >
                  <img
                    src={page === 1 ? pageLeftHide : pageLeft}
                    alt="Prev"
                    className="pagination-img"
                  />
                </button>
                <div className="pagination-center">
                  <img src={pageCenter} alt="Page" className="pagination-img" />
                  <span className="pagination-number" style={{ color: "white" }}>
                    {page}
                  </span>
                </div>
                <button
                  type="button"
                  aria-label="Next"
                  className={`pagination-btn ${
                    page >= totalPages ? "disabled" : ""
                  }`}
                  onClick={() => {
                    if (page < totalPages) {
                      savedSelectionRef.current = saveSelection;
                      shouldRestoreSelectionRef.current = true;
                      setPage(page + 1);
                    }
                  }}
                >
                  <img src={pageRight} alt="Next" className="pagination-img" />
                </button>
              </div>
            </div>
          ) : null;
        })()}
      </div>
    </Fragment>
  );
};

export default MyStudentList;
